// EntityDocuments — DC-3 (2026-06-17, Chunk D). A reusable document lens for any
// non-claim business object (policy / company / contact / payee). Reads ONE
// document repository through the document_links junction via the generic
// /entities/{type}/{id}/documents routes. Same upload/list/download/delete UX as
// the claim Documents tab, but entity-scoped. Visibility/edit gating is enforced
// server-side (TPA-wall-strictest resolver); `canEdit` here only hides the
// upload/delete affordances for users who can't write.
import { useState, useEffect } from "react";
import { apiFetch, fmt } from "../api";

const TYPE_LABEL = { policy: "policy", company: "company", contact: "contact", payee: "payee" };

export default function EntityDocuments({ entityType, entityId, canEdit = false, defaultPhi = false, title = "Documents" }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const base = `/entities/${entityType}/${entityId}/documents`;
  const load = () => apiFetch(base).then(d => setDocs(d || [])).catch(() => setDocs([]));
  useEffect(() => { if (entityId) load(); /* eslint-disable-next-line */ }, [entityType, entityId]);
  const fsize = (b) => b == null ? "—" : b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB";
  const upload = async (file) => {
    if (!file) return; setBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("is_phi", defaultPhi ? "true" : "false");
      await apiFetch(base, { method: "POST", raw: true, body: fd }); load();
    } catch (e) { setErr(e.message || "Upload failed."); } finally { setBusy(false); }
  };
  const download = async (d) => {
    try {
      const res = await apiFetch(`/documents/${d.id}/download`, { raw: true }); const blob = await res.blob();
      const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = d.original_filename || "document";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch (e) { alert(e.message || "Download failed."); }
  };
  const del = async (d) => {
    if (!window.confirm(`Delete "${d.original_filename}"? This soft-deletes the document itself (not just its link to this ${TYPE_LABEL[entityType] || "record"}) — it will disappear everywhere it's linked.`)) return;
    try { await apiFetch(`/documents/${d.id}`, { method: "DELETE" }); load(); } catch (e) { alert(e.message || "Delete failed."); }
  };
  return (<div>
    <div className="section-header">
      <span className="section-title">{title}</span>
      {canEdit && <label className="btn btn-blue btn-sm" style={{ cursor: "pointer" }}>
        {busy ? "Uploading…" : "+ Upload"}
        <input type="file" style={{ display: "none" }} disabled={busy}
               onChange={e => { const f = e.target.files[0]; e.target.value = ""; upload(f); }} />
      </label>}
    </div>
    {err && <div className="alert-error" style={{ marginBottom: 10 }}>{err}</div>}
    <div className="card">
      {docs.length === 0
        ? <div className="empty-state" style={{ padding: "32px 20px" }}>
            <div style={{ fontSize: 24, opacity: 0.4, marginBottom: 6 }}>{"📄"}</div>
            <div style={{ fontWeight: 600, color: "var(--text2)" }}>No documents yet</div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 3 }}>
              {canEdit ? "Upload PDFs, images, or office files for this record." : "Documents attached to this record will appear here."}
            </div>
          </div>
        : <table><thead><tr>
            <th>Name</th><th>Type</th><th className="text-right">Size</th><th>Uploaded by</th><th>Date</th><th></th>
          </tr></thead><tbody>
            {docs.map(d => (<tr key={d.id}>
              <td><button onClick={() => download(d)} style={{ background: "none", border: "none", color: "var(--blue)", fontWeight: 600, cursor: "pointer", padding: 0, textAlign: "left" }} title="Download / view">{d.original_filename || "—"}</button>
                {d.is_phi && <span className="badge" style={{ marginLeft: 6, background: "#fee2e2", color: "#991b1b", fontSize: 9.5 }}>PHI</span>}</td>
              <td style={{ fontSize: 12, color: "var(--text2)" }}>{d.document_type || "—"}</td>
              <td className="text-right mono" style={{ fontSize: 12 }}>{fsize(d.bytes_stored)}</td>
              <td style={{ fontSize: 12 }}>{d.uploaded_by_name || "—"}</td>
              <td style={{ fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>{fmt.date(d.created_at)}</td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <button onClick={() => download(d)} title="Download" style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 5px", color: "var(--text3)" }}>{"⤓"}</button>
                {canEdit && <button onClick={() => del(d)} title="Delete" style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 5px", color: "#dc2626" }}>{"🗑"}</button>}
              </td>
            </tr>))}
          </tbody></table>}
    </div>
  </div>);
}
